import { useState } from 'react';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { AccessTime, Language } from '@mui/icons-material';
import {
  useWorkRestProfileForLocationQuery,
  WorkRestIntensity,
  WorkRestProfile,
} from '../../../Services/API/workRestApi';
import { Card, Modal, useLocalObjectStorage } from '@perry-weather/component-library';
import { ProgressBar } from '../ProgressBar';
import { Observation } from '../../../Services/API/observationsApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { WorkRestDetailsModal } from './WorkRestDetailsModal';
import { formatTemperature, getCurrentObservationValue, getLabels, getWorkRestMatrix } from './WorkRestHelpers';
import { Link } from '../../Base/Link';
import { useGetRiskProfileByIdQuery } from '../../../Services/API/riskProfileApi';
import { getRiskLevelMeta } from './RiskProfile/RiskProfileHelpers';
import { withClickTracking } from '../../Tracked/withMixpanelTracking';
import { TrackableEvents } from '../../Tracked/events';
import { Thermometer } from '../../../Assets/Thermometer';

const TrackedLink = withClickTracking(Link)(TrackableEvents.ButtonClicked, {
  button_location: 'Dashboard',
  button_feature: 'Work Rest',
  button_label: 'View Details',
  button_type: 'link',
});

export interface WorkRestContainerProps {
  locationId?: string;
  observationData?: Observation;
  riskProfileId: number;
  subscribedLocationId: string;
}

export interface WorkRestCardProps extends WorkRestContainerProps {
  profile: WorkRestProfile;
}

export const defineWorkRestIcon = (workRestProfile: WorkRestProfile | undefined, color: string) => {
  return {
      WBGT: <Language fontSize='small' fill={color} />,
      Temperature: <Thermometer fontSize='small' color={color} />,
      'Heat Index': <Thermometer fontSize='small' color={color} />
  }[workRestProfile?.primaryMetric ?? 'WBGT'];
};

export function WorkRestContainer({
  observationData,
  locationId,
  riskProfileId,
  subscribedLocationId,
}: WorkRestContainerProps) {
  const { data: profile, isSuccess } = useWorkRestProfileForLocationQuery(locationId || skipToken);

  if (!isSuccess || !profile) return null;

  return (
    <WorkRestCard
      observationData={observationData}
      locationId={locationId}
      profile={profile}
      riskProfileId={riskProfileId}
      subscribedLocationId={subscribedLocationId}
    />
  );
}

export function WorkRestCard({
  observationData,
  locationId,
  profile,
  riskProfileId,
  subscribedLocationId,
}: WorkRestCardProps) {
  const theme = useTheme();
  const isDarkTheme = theme.palette.mode === 'dark';
  const iconColor = isDarkTheme ? theme.palette.text.primary : theme.palette.text.secondary;
  const cardIcon = defineWorkRestIcon(profile, iconColor);
  const [modalWorkRestDetailsOpen, setWorkRestDetailsModalOpen] = useState(false);
  const observationValue = getCurrentObservationValue(observationData, profile!) || 0;

  const SELECTED_MODIFIERS_KEY = `work_rest_selected_modifiers_${locationId}_${profile.id}`;
  const SELECTED_INTENSITY_KEY = `work_rest_selected_intensity_${locationId}_${profile.id}`;
  const [currentIntensity, setCurrentIntensity] = useLocalObjectStorage<WorkRestIntensity>(
    SELECTED_INTENSITY_KEY,
    profile.intensities[0]
  );

  const [selectedModifiers, setSelectedModifiers] = useLocalObjectStorage<number[]>(SELECTED_MODIFIERS_KEY, []);

  const totalAdjustment =
    profile?.modifiers
      .filter(mod => selectedModifiers.includes(mod.id))
      .reduce((acc, mod) => acc + mod.adjustment, 0) ?? 0;

  const adjustedObservationValue = observationValue + totalAdjustment;
  const { data: riskProfile, isSuccess } = useGetRiskProfileByIdQuery(riskProfileId || skipToken);
  const { name: riskLevelName, color: primaryColor, colorSecondary: secondaryColor } =
    profile.useRiskProfile && riskProfile && isSuccess
      ? getRiskLevelMeta(riskProfile.riskLevels, adjustedObservationValue)
      : {};

  const selectedMatrix = getWorkRestMatrix(profile, adjustedObservationValue, currentIntensity);

  const { leftLabel, rightLabel } = getLabels(profile!, selectedMatrix);
  const showAdjusted = adjustedObservationValue !== observationValue;
  const displayAdjusted = formatTemperature(adjustedObservationValue);
  const displayActual = formatTemperature(observationValue);  

  const handleModifierChange = (modifiers: number[]) => {
    setSelectedModifiers(modifiers);
  };

  const handleOpenDetailsModal = () => setWorkRestDetailsModalOpen(true);
  const handleCloseDetailsModal = () => setWorkRestDetailsModalOpen(false);

  return (
    <Card
      bottomActions={
        <TrackedLink onClick={handleOpenDetailsModal}>
          <Typography>View Details</Typography>
        </TrackedLink>
      }>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography fontWeight='bold'>Heat Stress</Typography>
        {riskLevelName && (
          <Box
            sx={{
              backgroundColor: secondaryColor,
              color: primaryColor,
              fontSize: '0.75rem',
              borderRadius: '999px',
              padding: '2px 12px',
              fontWeight: 'bold',
              border: `1px solid ${primaryColor}`,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
            }}>
            {riskLevelName} Risk
          </Box>
        )}
      </Box>

      <Grid container>
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: isDarkTheme ? 'rgba(212, 187, 255, 0.06)' : 'rgba(0, 0, 0, 0.02)',
              borderRadius: 2,
              padding: '12px 12px 24px 12px',
            }}>
            <Box display='flex' alignItems='center' gap={1}>
              {cardIcon}
              <Typography variant='body1' fontWeight='bold'>
                {profile.primaryMetric}
              </Typography>
            </Box>
            <Grid container>
              <Grid container justifyContent='center' item xs={5} md={5}>
                <Box display='flex' flexDirection='column' alignItems='start' justifyContent='center'>
                  {showAdjusted && <Typography color='textSecondary'>Adjusted</Typography>}
                  <Typography variant='h4' lineHeight={1.2} fontWeight='bold'>
                    {displayAdjusted}
                  </Typography>
                  {showAdjusted && (
                    <Typography color='textSecondary'>
                      Actual:{' '}
                      <Typography component='span' variant='body2' fontWeight='bold' color='textPrimary'>
                        {displayActual}
                      </Typography>
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={7} md={7}>
                <Box display='flex' flexDirection='column' gap={2}>
                  <Box display='flex' alignItems='center' gap={1}>
                    <AccessTime fontSize='small' color='action' sx={{ color: iconColor }} />
                    <Typography variant='body1' fontWeight='bold'>
                      Work / Rest Cycle
                    </Typography>
                  </Box>

                  {selectedMatrix && (
                    <ProgressBar
                      progress={selectedMatrix.workPercent}
                      leftLabel={leftLabel}
                      rightLabel={rightLabel}
                      primaryColor={{ background: '#B2EDFF', text: '#125D6E' }}
                      secondaryColor={{ background: '#036980', text: '#A9E5F7' }}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
      <Modal
        modalState={{
          open: modalWorkRestDetailsOpen,
          handleClose: handleCloseDetailsModal,
          handleOpen: handleOpenDetailsModal,
        }}>
        <Box>
          <WorkRestDetailsModal
            currentObservation={observationData!}
            workRestProfile={profile}
            selectedIntensity={currentIntensity!}
            onIntensityChange={setCurrentIntensity}
            selectedModifiers={selectedModifiers}
            onModifierChange={handleModifierChange}
            handleClose={handleCloseDetailsModal}
            riskProfile={riskProfile!}
            locationId={subscribedLocationId!}
          />
        </Box>
      </Modal>
    </Card>
  );
}
